import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Loader2, Share2, Printer, AlertTriangle, Award, Calendar, Bell } from "lucide-react";
import { toast } from "sonner";
import { Layout } from "@/components/Layout";
import { PriorityMatrix } from "@/components/charts/PriorityMatrix";
import { Input } from "@/components/ui/input";
import { getPaidResult, setReminder } from "@/lib/api";
import { Seo } from "@/components/Seo";

const SECTION_HEADERS = [
  "Overall Assessment",
  "Key Strength",
  "Critical Gap",
  "Three Prioritized Recommendations",
  "AI Transformation Opportunity",
];

const clean = (s) =>
  s.replace(/\*\*/g, "").replace(/^#{1,6}\s*/gm, "").replace(/^[-]{2,}$/gm, "")
   .replace(/^[:#\s*-]+/, "").replace(/[#\s]+$/, "").trim();

function parseReport(text) {
  if (!text) return [];
  const sections = [];
  const indices = SECTION_HEADERS
    .map((h) => ({ h, i: text.indexOf(h) }))
    .filter((x) => x.i >= 0)
    .sort((a, b) => a.i - b.i);
  if (indices.length === 0) return [{ title: "AI Diagnostic Report", body: clean(text) }];
  indices.forEach((cur, k) => {
    const start = cur.i + cur.h.length;
    const end = k + 1 < indices.length ? indices[k + 1].i : text.length;
    sections.push({ title: cur.h, body: clean(text.slice(start, end)) });
  });
  return sections;
}

// Expand the FIRST occurrence of OMS / PES into the full term (idempotent).
const expandAcronyms = (text) => {
  if (!text) return text;
  let out = text;
  if (!out.includes("Operational Maturity Score")) {
    out = out.replace(/\bOMS\b/, "Operational Maturity Score (OMS)");
  }
  if (!out.includes("Process Environment Score")) {
    out = out.replace(/\bPES\b/, "Process Environment Score (PES)");
  }
  return out;
};

const AI_CARDS = [
  { key: "data_foundation", title: "Data Foundation for AI", note: "How clean, integrated, and governed your planning data is — the bedrock for any AI initiative." },
  { key: "ai_tool_adoption", title: "AI Tool Adoption in Planning", note: "How far ML and AI are embedded in your day-to-day planning decisions today." },
  { key: "genai_readiness", title: "GenAI & Copilot Readiness", note: "Your readiness to deploy GenAI copilots for narratives, exceptions, and decision support." },
];

export default function PaidResults() {
  const { resultId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reminderEmail, setReminderEmail] = useState("");

  useEffect(() => {
    if (!resultId) { setLoading(false); return; }
    getPaidResult(resultId)
      .then(setData)
      .catch((e) => {
        if (e?.response?.status === 403) { toast.error("Payment required."); navigate(`/results/free/${resultId}`); }
        else toast.error("Couldn't load your report.");
      })
      .finally(() => setLoading(false));
  }, [resultId, navigate]);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-white"><Loader2 className="h-8 w-8 animate-spin text-[#1A6B5A]" /></div>;

  if (!data) {
    return (
      <Layout>
        <div className="max-w-xl mx-auto px-6 py-32 text-center">
          <h1 className="font-heading text-3xl font-bold text-[#1B3A6B] mb-4">Report unavailable</h1>
          <button onClick={() => navigate("/scan")} className="bg-[#1A6B5A] text-white rounded-sm px-7 py-3.5 font-semibold">Take the assessment</button>
        </div>
      </Layout>
    );
  }

  const { results, profile, report_text, report_ok, created_at } = data;
  const { overall, functions, matrix_l3, ai_readiness } = results;
  const allL2 = functions.flatMap((f) => f.l2);
  const strength = [...allL2].sort((a, b) => a.oms - b.oms)[0];
  const gap = [...allL2].sort((a, b) => b.oms - a.oms)[0];
  const sections = parseReport(expandAcronyms(report_text));
  const recsSection = sections.find((s) => s.title === "Three Prioritized Recommendations");
  const recItems = recsSection
    ? recsSection.body.split(/\n+(?=\d[\.\)])/).map((s) => s.replace(/^\d[\.\)]\s*/, "").trim()).filter(Boolean).slice(0, 3)
    : [];

  const share = async () => {
    try { await navigator.clipboard.writeText(window.location.href); toast.success("Report link copied to clipboard."); }
    catch { toast.error("Couldn't copy link."); }
  };

  const saveReminder = async () => {
    if (!reminderEmail) return;
    try { const r = await setReminder(resultId, reminderEmail); toast.success(r.message); setReminderEmail(""); }
    catch { toast.error("Couldn't set reminder."); }
  };

  const dateStr = new Date(created_at).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });

  return (
    <Layout>
      <Seo
        title="Your AI Diagnostic Report | SCMBrain"
        description="Your full SCMBrain AI diagnostic report with executive summary, granular maturity matrix, and AI-readiness assessment."
        path="/results/paid"
        noindex
      />
      {/* HEADER */}
      <section className="border-b border-[#E5E7EB]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 py-10 flex flex-col sm:flex-row justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-[#1A6B5A] font-semibold">Full AI Diagnostic Report</p>
            <h1 className="font-heading text-3xl font-extrabold text-[#1B3A6B] mt-1">SCM Planning Maturity</h1>
          </div>
          <div className="text-sm text-[#3E577A] sm:text-right">
            <div className="font-semibold text-[#1B3A6B]">{profile.name}</div>
            <div>{profile.company}</div>
            <div className="text-[#94A3B8]">{dateStr}</div>
          </div>
        </div>
      </section>

      {/* EXECUTIVE SUMMARY */}
      <section className="max-w-4xl mx-auto px-6 md:px-12 py-12">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-heading text-2xl font-bold text-[#1B3A6B]">Executive Summary</h2>
          <div className="flex gap-2 print:hidden">
            <button data-testid="share-summary-btn" onClick={share} className="inline-flex items-center gap-2 text-sm border border-[#E5E7EB] rounded-sm px-4 py-2 text-[#3E577A] hover:border-[#1A6B5A]">
              <Share2 className="h-4 w-4" /> Share
            </button>
            <button data-testid="print-btn" onClick={() => window.print()} className="inline-flex items-center gap-2 text-sm border border-[#E5E7EB] rounded-sm px-4 py-2 text-[#3E577A] hover:border-[#1A6B5A]">
              <Printer className="h-4 w-4" /> Print / PDF
            </button>
          </div>
        </div>

        <div className="bg-[#F4F4F4] border border-[#E5E7EB] rounded-sm p-7">
          <div className="flex items-center gap-4 pb-5 border-b border-[#E5E7EB]">
            <span className="font-heading text-5xl font-extrabold" style={{ color: overall.level.color }}>{overall.oms}</span>
            <div>
              <div className="text-xs text-[#94A3B8]">Overall maturity / 9.0</div>
              <div className="inline-block mt-1 px-3 py-1 rounded-full text-white text-xs font-semibold" style={{ background: overall.level.color }}>
                Level {overall.level.number} · {overall.level.name}
              </div>
            </div>
          </div>
          <p className="text-xs text-[#94A3B8] mt-4">
            <span className="font-semibold text-[#3E577A]">Operational Maturity Score (OMS)</span> reflects process
            maturity (lower is more mature); <span className="font-semibold text-[#3E577A]">Process Environment Score (PES)</span> reflects
            complexity.
          </p>
          <div className="grid sm:grid-cols-2 gap-5 mt-5">
            <div className="flex items-start gap-3">
              <Award className="h-5 w-5 text-[#1A6B5A] mt-0.5 shrink-0" />
              <div><div className="text-xs font-semibold text-[#1A6B5A] uppercase tracking-wider">Top strength</div>
                <div className="text-sm text-[#1B3A6B] mt-1">{strength?.name} — your most mature area (OMS {strength?.oms}).</div></div>
            </div>
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-[#EF4444] mt-0.5 shrink-0" />
              <div><div className="text-xs font-semibold text-[#EF4444] uppercase tracking-wider">Most critical gap</div>
                <div className="text-sm text-[#1B3A6B] mt-1">{gap?.name} — your least mature area (OMS {gap?.oms}).</div></div>
            </div>
          </div>
          {recItems.length > 0 && (
            <div className="mt-6">
              <div className="text-xs font-semibold text-[#1B3A6B] uppercase tracking-wider mb-3">Prioritized recommendations</div>
              <ol className="space-y-2">
                {recItems.map((r, i) => (
                  <li key={i} className="flex gap-3 text-sm text-[#3E577A]">
                    <span className="font-bold text-[#1A6B5A]">{i + 1}.</span>
                    <span>{r.length > 220 ? r.slice(0, 220) + "…" : r}</span>
                  </li>
                ))}
              </ol>
            </div>
          )}
        </div>
      </section>

      {/* FULL REPORT */}
      <section className="max-w-4xl mx-auto px-6 md:px-12 pb-12">
        <h2 className="font-heading text-2xl font-bold text-[#1B3A6B] mb-6">Full AI Diagnostic Report</h2>
        {!report_ok && (
          <div className="bg-[#FEF3C7] border border-[#F59E0B]/40 rounded-sm p-4 text-sm text-[#92400E] mb-6">
            {report_text}
          </div>
        )}
        {report_ok && (
          <div className="space-y-7">
            {sections.map((s) => (
              <div key={s.title} data-testid="report-section">
                <h3 className="font-heading text-lg font-bold text-[#1B3A6B] mb-2">{s.title}</h3>
                <div className="text-[#3E577A] leading-relaxed whitespace-pre-line text-[15px]">{s.body}</div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* GRANULAR MATRIX */}
      <section className="bg-[#F4F4F4] border-y border-[#E5E7EB]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 py-12">
          <h2 className="font-heading text-2xl font-bold text-[#1B3A6B] mb-2">Full Transformation Priority Matrix</h2>
          <p className="text-[#3E577A] text-sm mb-6">Every individual process activity plotted — see exactly which processes drive your risk.</p>
          <div className="bg-white border border-[#E5E7EB] rounded-sm p-4 md:p-8">
            <PriorityMatrix data={matrix_l3} height={520} />
          </div>
        </div>
      </section>

      {/* AI READINESS */}
      <section className="max-w-4xl mx-auto px-6 md:px-12 py-12">
        <h2 className="font-heading text-2xl font-bold text-[#1B3A6B] mb-6">AI Readiness Breakdown</h2>
        <div className="grid sm:grid-cols-3 gap-4">
          {AI_CARDS.map((c) => {
            const block = ai_readiness[c.key];
            return (
              <div key={c.key} data-testid="ai-readiness-card" className="border border-[#E5E7EB] rounded-sm p-6 bg-white">
                <div className="flex items-baseline gap-2">
                  <span className="font-heading text-3xl font-extrabold" style={{ color: block.level.color }}>{block.score}</span>
                  <span className="text-[#94A3B8] text-sm">/ 9.0</span>
                </div>
                <div className="inline-block mt-2 mb-3 px-2 py-0.5 rounded-full text-white text-[11px] font-semibold" style={{ background: block.level.color }}>
                  Level {block.level.number} · {block.level.name}
                </div>
                <h3 className="font-heading font-bold text-[#1B3A6B] text-sm">{c.title}</h3>
                <p className="text-xs text-[#3E577A] mt-1 leading-relaxed">{c.note}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* NEXT ASSESSMENT */}
      <section className="bg-[#1B3A6B]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 py-14 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <Calendar className="h-8 w-8 text-[#1A6B5A]" />
            <div>
              <h3 className="font-heading text-xl font-bold text-white">Your next quarterly re-assessment is available in 90 days.</h3>
              <p className="text-[#C5D2E6] text-sm mt-1">Track your maturity progress over time.</p>
            </div>
          </div>
          <div className="flex w-full md:w-auto gap-2">
            <Input data-testid="reminder-email-input" type="email" placeholder="Email for reminder"
              value={reminderEmail} onChange={(e) => setReminderEmail(e.target.value)} className="bg-white border-0 w-full md:w-56" />
            <button data-testid="set-reminder-btn" onClick={saveReminder}
              className="inline-flex items-center gap-2 bg-[#1A6B5A] text-white rounded-sm px-5 py-2.5 text-sm font-semibold whitespace-nowrap hover:bg-[#135245]">
              <Bell className="h-4 w-4" /> Set reminder
            </button>
          </div>
        </div>
      </section>
    </Layout>
  );
}
